import { Pause, Play, Volume1, Volume2, VolumeX } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/utils/cn'

interface AudioPlayerProps {
  src: string
  label?: string
  className?: string
  seek?: { seconds: number }
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function AudioPlayer({ src, label, className, seek }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const [volumeOpen, setVolumeOpen] = useState(false)
  const [scrubbing, setScrubbing] = useState(false)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onLoaded = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0)
    const onTime = () => {
      if (!scrubbing) setCurrentTime(audio.currentTime)
    }
    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    const onEnded = () => setIsPlaying(false)
    const onVolume = () => {
      setVolume(audio.volume)
      setMuted(audio.muted)
    }

    audio.addEventListener('loadedmetadata', onLoaded)
    audio.addEventListener('durationchange', onLoaded)
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('volumechange', onVolume)
    // A cached blob may have loaded before this effect subscribed (e.g. a lazy preview).
    onLoaded()
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded)
      audio.removeEventListener('durationchange', onLoaded)
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('volumechange', onVolume)
    }
  }, [scrubbing])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !seek || !Number.isFinite(seek.seconds)) return
    audio.currentTime = Math.max(0, seek.seconds)
    setCurrentTime(audio.currentTime)
    void audio.play().catch(() => setIsPlaying(false))
  }, [seek])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false))
    } else {
      audio.pause()
    }
  }

  const handleSeek = (value: number) => {
    const audio = audioRef.current
    if (!audio || !Number.isFinite(audio.duration)) return
    audio.currentTime = value
    setCurrentTime(value)
  }

  const handleVolume = (value: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = value
    audio.muted = value === 0
  }

  const toggleMute = () => {
    const audio = audioRef.current
    if (!audio) return
    audio.muted = !audio.muted
  }

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2

  return (
    <div
      data-slot="audio-player"
      data-playing={isPlaying ? 'true' : undefined}
      className={cn(
        // Glass shell
        'group/audio relative isolate overflow-hidden rounded-3xl',
        'border border-border/50',
        'bg-gradient-to-br from-card/70 via-card/50 to-card/30',
        'backdrop-blur-2xl backdrop-saturate-150',
        'shadow-[0_8px_32px_-12px_rgb(0_0_0/0.35),inset_0_1px_0_0_rgb(255_255_255/0.08)]',
        'transition-shadow duration-500',
        'hover:shadow-[0_12px_40px_-12px_rgb(0_0_0/0.45),inset_0_1px_0_0_rgb(255_255_255/0.12)]',
        className
      )}
    >
      {/* Specular highlight strip */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-foreground/40 to-transparent opacity-60"
      />
      {/* Soft color wash that responds to play state */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-700',
          'bg-gradient-to-r from-primary/15 via-primary/5 to-transparent',
          isPlaying && 'opacity-100'
        )}
      />
      {/* Grain / micro-texture for depth */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.025] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <audio ref={audioRef} src={src} preload="metadata" aria-label={label} className="hidden">
        <track kind="captions" />
      </audio>

      <div className="relative flex items-center gap-4 px-4 py-3.5 sm:gap-5 sm:px-5">
        <PlayButton isPlaying={isPlaying} onClick={togglePlay} />

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Scrubber
            value={currentTime}
            max={duration}
            onSeek={handleSeek}
            onScrubStart={() => setScrubbing(true)}
            onScrubEnd={() => setScrubbing(false)}
            playing={isPlaying}
          />
          <div className="flex items-center justify-between font-mono text-[10.5px] tabular-nums text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <VolumeControl
          icon={<VolumeIcon className="h-4 w-4" />}
          volume={muted ? 0 : volume}
          open={volumeOpen}
          onOpen={() => setVolumeOpen(true)}
          onMute={toggleMute}
          onChange={handleVolume}
          onClose={() => setVolumeOpen(false)}
        />
      </div>
    </div>
  )
}

interface PlayButtonProps {
  isPlaying: boolean
  onClick: () => void
}

function PlayButton({ isPlaying, onClick }: PlayButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPlaying ? 'Pause' : 'Play'}
      className={cn(
        'relative grid h-11 w-11 shrink-0 place-items-center rounded-full',
        'bg-gradient-to-br from-primary to-primary/80 text-primary-foreground',
        'shadow-[0_6px_20px_-6px_var(--color-primary),inset_0_1px_0_0_rgb(255_255_255/0.25)]',
        'ring-1 ring-inset ring-foreground/10',
        'transition-all duration-300 ease-out',
        'hover:scale-[1.04] hover:shadow-[0_8px_24px_-6px_var(--color-primary),inset_0_1px_0_0_rgb(255_255_255/0.3)]',
        'active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card'
      )}
    >
      {/* Pulse ring while playing */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-0 rounded-full ring-1 ring-primary/40',
          isPlaying && 'animate-ping opacity-40',
          !isPlaying && 'opacity-0'
        )}
      />
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-gradient-to-b from-foreground/15 to-transparent opacity-50"
      />
      {isPlaying ? (
        <Pause className="relative h-[18px] w-[18px] fill-current" strokeWidth={0} />
      ) : (
        <Play
          className="relative h-[18px] w-[18px] translate-x-[1px] fill-current"
          strokeWidth={0}
        />
      )}
    </button>
  )
}

interface ScrubberProps {
  value: number
  max: number
  onSeek: (value: number) => void
  onScrubStart: () => void
  onScrubEnd: () => void
  playing: boolean
}

function Scrubber({ value, max, onSeek, onScrubStart, onScrubEnd, playing }: ScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const [hover, setHover] = useState(false)

  const seekFromPointer = (clientX: number) => {
    const el = trackRef.current
    if (!el || max <= 0) return
    const rect = el.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    onSeek(ratio * max)
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (max <= 0) return
    draggingRef.current = true
    onScrubStart()
    ;(e.target as Element).setPointerCapture(e.pointerId)
    seekFromPointer(e.clientX)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    seekFromPointer(e.clientX)
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    onScrubEnd()
    try {
      ;(e.target as Element).releasePointerCapture(e.pointerId)
    } catch {
      // capture may already be released
    }
  }

  const progress = max > 0 ? (value / max) * 100 : 0

  return (
    <div
      ref={trackRef}
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={max || 0}
      aria-valuenow={value}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onKeyDown={e => {
        if (max <= 0) return
        const step = Math.max(1, max / 100)
        if (e.key === 'ArrowRight') onSeek(Math.min(max, value + step))
        if (e.key === 'ArrowLeft') onSeek(Math.max(0, value - step))
      }}
      className={cn(
        'group/scrub relative h-6 cursor-pointer touch-none select-none',
        'focus-visible:outline-none'
      )}
    >
      {/* Track */}
      <div
        className={cn(
          'absolute top-1/2 left-0 right-0 -translate-y-1/2 overflow-hidden rounded-full',
          'bg-foreground/10 transition-all duration-200',
          hover ? 'h-[5px]' : 'h-[3px]'
        )}
      >
        {/* Buffered / wash background hint */}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/0 via-foreground/[0.03] to-foreground/0" />
        {/* Filled */}
        <div
          className={cn(
            'relative h-full rounded-full',
            'bg-gradient-to-r from-primary/70 via-primary to-primary',
            'shadow-[0_0_12px_-2px_var(--color-primary)]'
          )}
          style={{ width: `${progress}%` }}
        >
          {/* Shimmer while playing */}
          {playing && (
            <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-r from-transparent via-foreground/30 to-transparent opacity-50 mix-blend-overlay" />
          )}
        </div>
      </div>

      {/* Thumb */}
      <div
        className={cn(
          'absolute top-1/2 -translate-x-1/2 -translate-y-1/2',
          'h-3.5 w-3.5 rounded-full',
          'bg-card border border-primary',
          'shadow-[0_2px_8px_-1px_rgb(0_0_0/0.4)]',
          'transition-all duration-200',
          hover ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        )}
        style={{ left: `${progress}%` }}
        aria-hidden
      />
    </div>
  )
}

interface VolumeControlProps {
  icon: React.ReactNode
  volume: number
  open: boolean
  onOpen: () => void
  onMute: () => void
  onChange: (value: number) => void
  onClose: () => void
}

function VolumeControl({
  icon,
  volume,
  open,
  onOpen,
  onMute,
  onChange,
  onClose,
}: VolumeControlProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  const seekFromPointer = (clientX: number) => {
    const el = trackRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    onChange(ratio)
  }

  return (
    <div
      className="relative flex shrink-0 items-center"
      onPointerEnter={onOpen}
      onPointerLeave={() => {
        if (!draggingRef.current) onClose()
      }}
    >
      <button
        type="button"
        onClick={onMute}
        aria-label="Toggle mute"
        className={cn(
          'grid h-9 w-9 place-items-center rounded-full text-muted-foreground',
          'transition-colors duration-200',
          'hover:bg-foreground/5 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        {icon}
      </button>
      <div
        className={cn(
          'flex items-center overflow-hidden transition-[width,opacity] duration-300 ease-out',
          open ? 'w-20 opacity-100' : 'w-0 opacity-0'
        )}
      >
        <div
          ref={trackRef}
          role="slider"
          tabIndex={0}
          aria-label="Volume"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(volume * 100)}
          onPointerDown={e => {
            draggingRef.current = true
            ;(e.target as Element).setPointerCapture(e.pointerId)
            seekFromPointer(e.clientX)
          }}
          onPointerMove={e => {
            if (draggingRef.current) seekFromPointer(e.clientX)
          }}
          onPointerUp={e => {
            draggingRef.current = false
            try {
              ;(e.target as Element).releasePointerCapture(e.pointerId)
            } catch {
              // already released
            }
          }}
          onKeyDown={e => {
            if (e.key === 'ArrowRight') onChange(Math.min(1, volume + 0.05))
            if (e.key === 'ArrowLeft') onChange(Math.max(0, volume - 0.05))
          }}
          className="relative h-5 w-full cursor-pointer touch-none select-none"
        >
          <div className="absolute top-1/2 left-0 right-0 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-foreground/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary/70 to-primary"
              style={{ width: `${volume * 100}%` }}
            />
          </div>
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary bg-card shadow-[0_2px_6px_-1px_rgb(0_0_0/0.4)]"
            style={{ left: `${volume * 100}%` }}
            aria-hidden
          />
        </div>
      </div>
    </div>
  )
}
